import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  deleteCategory,
  getCategories,
} from "../../redux/slices/categorySlice";
import { useEffect, useState } from "react";
import { TiPencil } from "react-icons/ti";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaThList } from "react-icons/fa";
import { Pagination } from "../../components/common/pagination/Pagination";
import { useQueryParams } from "../../hooks/useQueryParams";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";
import { ConfirmModal } from "../common/ConfirmModal";
import { PageHeader } from "../common/PageHeader";
import { SearchInput } from "../common/SearchInput";
import { EmptyState } from "../common/EmptyState";
import { StatusBadge } from "../common/StatusBadge";
import { DataTable } from "../common/DataTable";

type Payload = {
  onCreate: () => void;
  onUpdate: (id: string) => void;
};

export const ListCategory = ({ onCreate, onUpdate }: Payload) => {
  const dispatch = useAppDispatch();
  const { categories, pagination, loading } = useAppSelector(
    (state) => state.category,
  );
  const { getParam, updateParams } = useQueryParams();

  const page = Number(getParam("page", 1));
  const limit = Number(getParam("limit", 10));
  const isActive = getParam("isActive", "all");
  const search = getParam("search", "");

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateParams({ [name]: value, page: 1 });
  };

  const parserIsActive =
    isActive === "all" ? undefined : isActive === "true" ? true : false;
  useEffect(() => {
    dispatch(getCategories({ search, page, limit, isActive: parserIsActive }));
  }, [dispatch, search, page, limit, parserIsActive]);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await dispatch(deleteCategory(confirmDeleteId)).unwrap();
      toast.success("Xóa danh mục thành công");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="p-5">
      <PageHeader
        title="Quản lý danh mục"
        subtitle={pagination ? `Tổng ${pagination.total} danh mục` : undefined}
        actions={
          <button
            type="button"
            onClick={onCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 transition-colors cursor-pointer text-sm text-white rounded-lg font-medium"
          >
            + Thêm danh mục
          </button>
        }
      />

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="p-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 border-b border-gray-100">
          <SearchInput
            value={search}
            onChange={(v) => updateParams({ search: v, page: 1 })}
            placeholder="Tìm danh mục theo tên..."
            className="w-full lg:w-80"
          />
          <div className="flex items-center gap-2">
            <select
              name="isActive"
              value={isActive}
              onChange={handleChange}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="true">Hoạt động</option>
              <option value="false">Ngừng hoạt động</option>
            </select>
            <select
              name="limit"
              value={limit}
              onChange={handleChange}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="10">10 / trang</option>
              <option value="20">20 / trang</option>
              <option value="30">30 / trang</option>
              <option value="50">50 / trang</option>
            </select>
          </div>
        </div>

        <DataTable
          data={categories}
          rowKey={(c) => c._id}
          loading={loading.list}
          columns={[
            {
              key: "image",
              header: "Ảnh",
              align: "center",
              width: "80px",
              render: (c) => (
                <img
                  src={c.image?.url}
                  alt={c.name}
                  className="h-12 w-12 border border-gray-200 rounded-lg object-cover mx-auto"
                />
              ),
            },
            {
              key: "name",
              header: "Tên danh mục",
              align: "left",
              render: (c) => (
                <span className="font-medium text-gray-900">{c.name}</span>
              ),
            },
            {
              key: "parent",
              header: "Danh mục cha",
              align: "left",
              render: (c) => c.parentId?.name ?? "—",
            },
            {
              key: "level",
              header: "Cấp",
              align: "center",
              width: "60px",
              render: (c) => c.level,
            },
            {
              key: "status",
              header: "Trạng thái",
              align: "center",
              render: (c) =>
                c.isActive ? (
                  <StatusBadge tone="success" label="Hoạt động" />
                ) : (
                  <StatusBadge tone="danger" label="Ngừng" />
                ),
            },
            {
              key: "sortOrder",
              header: "Ưu tiên",
              align: "center",
              width: "80px",
              render: (c) => c.sortOrder,
            },
            {
              key: "actions",
              header: "Thao tác",
              align: "right",
              width: "100px",
              render: (c) => (
                <div className="flex items-center gap-1 justify-end">
                  <button
                    type="button"
                    title="Sửa"
                    onClick={() => onUpdate(c._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <TiPencil className="text-lg" />
                  </button>
                  <button
                    type="button"
                    title="Xóa"
                    onClick={() => setConfirmDeleteId(c._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <RiDeleteBinLine className="text-lg" />
                  </button>
                </div>
              ),
            },
          ]}
          empty={
            <EmptyState
              icon={<FaThList />}
              title={
                search
                  ? "Không tìm thấy danh mục nào"
                  : "Chưa có danh mục nào"
              }
              description={
                search
                  ? "Thử tìm với từ khóa khác"
                  : "Bắt đầu thêm danh mục đầu tiên"
              }
              action={
                !search ? (
                  <button
                    type="button"
                    onClick={onCreate}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm text-white cursor-pointer"
                  >
                    + Thêm danh mục
                  </button>
                ) : undefined
              }
            />
          }
        />
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center mt-4">
          <Pagination
            pagination={pagination}
            onChange={(newPage) => updateParams({ page: newPage })}
          />
        </div>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          message="Bạn có chắc chắn muốn xóa danh mục này? Hành động này không thể hoàn tác."
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};
